/**
 * PageLayout - Standard layout wrapper for ALL desktop pages
 * 
 * RULES:
 * 1. Tutte le pagine usano max-w-6xl (1152px) per contenuto
 * 2. Padding consistente: p-6 su mobile, p-8 su desktop
 * 3. NO freccia indietro su desktop (il menu è sempre visibile)
 * 4. Header con titolo nella stessa posizione per tutte le pagine
 * 5. Background: bg-[#FAFAFA] (light) / bg-[#1D1D1F] (dark)
 */

import { useNavigate } from "react-router-dom";

// Standard max-width for all pages
const STANDARD_MAX_WIDTH = "max-w-6xl"; // 1152px - optimal for reading and layout
const STANDARD_PADDING = "px-6 lg:px-8 py-6 lg:py-8";

export default function PageLayout({ 
  children, 
  className = "",
  noPadding = false
}) {
  return (
    <div className={`min-h-screen bg-[#FAFAFA] dark:bg-[#1C1C1E] pb-32 lg:pb-8 ${className}`}>
      <div className={`${STANDARD_MAX_WIDTH} mx-auto ${noPadding ? '' : STANDARD_PADDING}`}>
        {children}
      </div>
    </div>
  );
}

/**
 * PageHeader - Standard header for all pages
 * 
 * @param title - Main title (required)
 * @param subtitle - Optional subtitle  
 * @param icon - Optional icon component (renders with consistent styling)
 * @param action - Optional action button (right side)
 * @param showBackButton - Show back button on mobile (default: true)
 * @param onBack - Custom back handler (default: navigate(-1))
 */
export function PageHeader({ 
  title, 
  subtitle,
  icon: Icon,
  iconColor = "#FF6B4A",
  action,
  className = "",
  showBackButton = true,
  onBack
}) {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <header className={`mb-6 lg:mb-8 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 lg:gap-4">
          {/* Back button - mobile only */}
          {showBackButton && (
            <button
              onClick={handleBack}
              className="lg:hidden w-10 h-10 rounded-xl bg-white dark:bg-white/10 flex items-center justify-center shadow-sm border border-black/5 dark:border-white/10 hover:bg-[#F5F5F7] dark:hover:bg-white/20 transition-all"
              aria-label="Torna indietro"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1D1D1F] dark:text-white">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>
          )}
          {Icon && (
            <div 
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${iconColor}15` }}
            >
              <Icon size={20} className="lg:hidden" style={{ color: iconColor }} />
              <Icon size={24} className="hidden lg:block" style={{ color: iconColor }} />
            </div>
          )}
          <div>
            <h1 className="text-xl lg:text-3xl font-bold text-[#1D1D1F] dark:text-white">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs lg:text-sm text-[#86868B] dark:text-gray-400 mt-0.5 lg:mt-1">{subtitle}</p>
            )}
          </div>
        </div>
        {action && <div>{action}</div>}
      </div>
    </header>
  );
}

/**
 * Card - Standard card component
 */
export function Card({ children, className = "", onClick, hover = true, padding = "p-4 lg:p-6" }) {
  return (
    <div 
      onClick={onClick}
      className={`bg-white dark:bg-white/5 rounded-2xl ${padding} border border-black/5 dark:border-white/10 ${hover && onClick ? 'cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * StatCard - For displaying statistics with icon
 */
export function StatCard({ label, value, icon: Icon, color = "#FF6B4A", onClick }) {
  return (
    <Card onClick={onClick} className="flex items-center gap-4">
      <div 
        className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl lg:text-3xl font-bold text-[#1D1D1F] dark:text-white">{value}</p>
        <p className="text-sm text-[#86868B] dark:text-gray-400">{label}</p>
      </div>
    </Card>
  );
}

/**
 * SectionTitle - For section headings within a page
 */
export function SectionTitle({ children, className = "" }) {
  return (
    <h2 className={`text-lg lg:text-xl font-semibold text-[#1D1D1F] dark:text-white mb-4 ${className}`}>
      {children}
    </h2>
  );
}

/**
 * EmptyState - Consistent empty state display
 */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <Card className="text-center py-12">
      {Icon && (
        <div className="w-16 h-16 rounded-full bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Icon className="text-[#86868B]" size={32} />
        </div>
      )}
      <h3 className="text-lg font-semibold text-[#1D1D1F] dark:text-white mb-2">{title}</h3>
      <p className="text-[#86868B] dark:text-gray-400 mb-6 max-w-sm mx-auto">{description}</p>
      {action}
    </Card>
  );
}
